import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as IAction from 'app/interfaces/actiontracker.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { NotificationsService } from 'app/services/notifications.service';
import { CRPSteps } from 'modules/promptuary/constants/crp-steps';
import { IAppointment } from 'shared/interfaces/appointment.interface';
import { IPromptuary } from 'shared/interfaces/promptuary.interface';
import { AppointmentService } from 'shared/services/appointment/appointment.service';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';

import { ServiceEvolutionQuality } from '../../service-evolution-quality-banner/service-evolution-quality-banner.component';
import { UnifiedPromptuaryService } from '../unified-promptuary.service';

@Component({
	selector: 'promptuary-form',
	templateUrl: './promptuary-form.component.html',
	styleUrls: ['./promptuary-form.component.scss'],
})
export class PromptuaryForm implements OnInit {
	alarmSignals = false;
	appointment: IAppointment;
	autoSaveData;
	stepsComplete = false;
	formSteps = CRPSteps;
	isQualityModalVisible = true;
	quality: ServiceEvolutionQuality = 'default';
	tabIndex = 0;
	tabList = [
		{ name: 'PROMPTUARY.TABS.REGISTER' },
		{
			name: 'PROMPTUARY.TABS.TASK_TITLE',
			tagValue: 'PROMPTUARY.TABS.TASK_TAG',
		},
	];
	public firstName: string;

	@Input() from;
	@Input() mainType;
	@Input() type: 'page' | 'sidebar' = 'page';
	@Input() appointmentId: string;
	@Input() clientId: string;
	@Input() isAssistanceJourney = false;
	@Input() promptuary: IPromptuary;
	@Input() psychologist = false;
	@Input() psychotherapist = false;
	@Output('pageLoaded') emPageLoaded = new EventEmitter<boolean>();
	@Output() onRegister = new EventEmitter<any>();
	@Output() cancel = new EventEmitter<any>();
	constructor(
		public actionTrackerService: ActionTrackerService,
		public appointmentService: AppointmentService,
		public notificationService: NotificationsService,
		public promptuaryService: PromptuaryService,
		public router: Router,
		public translateService: TranslateService,
		public unifiedPromptuary: UnifiedPromptuaryService
	) {}

	ngOnInit(): void {
		this.appointmentService.getData(this.appointmentId).subscribe((result) => {
			this.appointment = result;
			if (this.appointment.userName) {
				this.firstName = this.appointment.userName.split(' ')[0];
			}
			this.unifiedPromptuary.appointmentId = this.appointmentId;
			this.autoSaveData = {
				id: this.appointmentId,
				contact: {
					id: this.appointment.profileId,
				},
			};
			this.unifiedPromptuary.setFormModel(this.promptuary, this.appointment);
			this.setAlarmSignals(Boolean(this.promptuary?.alarmSignals));
			this.emPageLoaded.emit(true);
			if (!this.isAssistanceJourney) {
				this.formSteps.alarmSignals.complete = true;
			}
		});
		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Promptuary,
			IAction.SUBSCOPE_TYPE.Session,
			this.type === 'page' ? IAction.ITEM_TYPE.Page : IAction.ITEM_TYPE.Sidebar,
			IAction.INTERACTION_TYPE.View,
			{
				specialist_type: this.mainType,
				from: this.from,
			}
		);

		this.openQualityModal();
	}

	openQualityModal() {
		this.isQualityModalVisible =
			localStorage.getItem('hidePromptuaryQualityModal') === null;
	}

	setAlarmSignals(alarmSignals: boolean) {
		this.alarmSignals = alarmSignals;
		this.updateStep(
			'alarmSignals',
			alarmSignals !== undefined && alarmSignals !== null
		);
	}

	goBack() {
		if (history.state?.returnTo) {
			this.router.navigate([history.state.returnTo]);
			return;
		}

		this.appointment.contactId
			? this.router.navigate([`/contact/${this.appointment.contactId}`])
			: this.router.navigate(['/clients']);
	}

	updateStep(stepName: string, newValue: string | boolean) {
		this.formSteps[stepName].complete = newValue ? true : false;

		this.stepsComplete = Object.values(this.formSteps).every(
			(elem) => elem.complete === true
		);
	}

	setPromptuaryValue(property: string, value: string | string[] | boolean) {
		this.unifiedPromptuary.setAutoSaveLoading(property);
		const promptData: IPromptuary = {
			customerId: this.clientId,
			eventId: this.appointmentId,
			[property]: value,
		};

		this.promptuaryService.autoSaveField(promptData).subscribe(
			() => {
				this.unifiedPromptuary.autosaveComplete(property);
			},
			({ error }) => {
				this.notificationService.showError(error.messageI18n);
			}
		);

		if (property === 'alarmSignals') {
			this.updateStep(property, true);
			return;
		}

		if (this.formSteps[property]) {
			this.updateStep(property, Boolean(value));
		}
	}

	updateNoshow(noshowStatus: boolean): void {
		this.appointment.noShow = noshowStatus;
	}

	changeTab(tabIndex: number) {
		this.tabIndex = tabIndex;

		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Promptuary,
			IAction.SUBSCOPE_TYPE.MenuItem,
			tabIndex === 1
				? IAction.ITEM_TYPE.Task
				: IAction.ITEM_TYPE.DocumentaryRecord,
			IAction.INTERACTION_TYPE.Click
		);
	}

	setAlarmsSignals(value: boolean): void {
		this.unifiedPromptuary.form.get('alarmSignals')?.setValue(value);
		if (!value) {
			this.unifiedPromptuary.form.get('alarmSignalsReason')?.reset();
		}
	}
}
